import { useState } from 'react';

import {
  Alert,
  ButtonMantine,
  CollapsibleCard,
  PasswordWithStrength,
  Stack,
  Text,
  TextInput,
  validatePassword,
} from 'ui';
import { isEmail, useForm } from 'ui/Form';
import { IconGivebacks } from 'ui/Icons';

import { User, UserCreateAccountRequest, useCreateNewUserAccount } from '../../../api';
import { ResetPasswordModal } from './ResetPasswordModal';
import { AuthFormProps } from './userAuthCard.types';

type CreateAccountProps = {
  cause_uuid?: string;
  initialEmail?: string;
  onSuccess?: (user: User) => void;
  toggleSignIn?: () => void;
  isEmailDisabled?: boolean;
  signInButton?: (onClick: () => void, isLoading: boolean) => React.ReactNode;
};
interface ErrorCreateAccountForm {
  status: boolean;
  message: string | undefined;
}
const initialErrorState = { status: false, message: '' };

const CreateAccountForm = ({
  cause_uuid,
  initialEmail,
  onSuccess,
  toggleSignIn,
  isEmailDisabled,
  signInButton,
}: CreateAccountProps) => {
  const { mutate: createAccount, isPending: isLoading } = useCreateNewUserAccount();
  const [errors, setErrors] = useState<ErrorCreateAccountForm>(initialErrorState);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);

  const form = useForm<AuthFormProps>({
    initialValues: {
      email: initialEmail || '',
      password: '',
    },
    validate: {
      email: isEmail('Email is invalid'),
      password: validatePassword,
    },
    validateInputOnBlur: true,
  });

  const handleCreateAccount = () => {
    if (form.validate().hasErrors) {
      return false;
    } else {
      const { email, password } = form.values;
      const requestObject: UserCreateAccountRequest = {
        user: {
          email,
          password,
          first_name: '',
          last_name: '',
          password_confirmation: password,
        },
      };
      cause_uuid && Object.assign(requestObject, { cause: cause_uuid });
      createAccount(requestObject, {
        onSuccess: (data) => {
          onSuccess && onSuccess(data);
        },
        onError: (e: Error) => {
          const error = e as { response?: { data?: { error?: string }; status?: number } };
          if (error.response?.status === 422) {
            setErrors({
              status: true,
              message: undefined,
            });
          } else {
            setErrors({
              status: true,
              message: error?.response?.data?.error ?? 'Unknown error occurred, please try again.',
            });
          }
        },
      });
    }
  };

  return (
    <>
      <Stack gap="lg" py={'md'}>
        <Stack gap={'xs'}>
          <TextInput
            placeholder="Email Address"
            leftSection={<IconGivebacks />}
            // TODO: Make this Integrated Checkout Only
            disabled={isEmailDisabled && !!initialEmail} // this should be specific to integrated checkout ?? will we allow users to change the email from donations?
            {...form.getInputProps('email')}
          />
          <PasswordWithStrength
            placeholder="Create a Password"
            {...form.getInputProps('password')}
          />
          <Alert.Transition type="error" mounted={errors.status}>
            {errors.message ? (
              errors.message
            ) : (
              <Text c={'cerise.7'}>
                This email has already been registered.{' '}
                <Text
                  fw={700}
                  span
                  c={'cerise.7'}
                  td={'underline'}
                  style={{ cursor: 'pointer' }}
                  onClick={toggleSignIn}
                >
                  {'Sign In'}
                </Text>
                {', '} or if you forgot your password, we can help you{' '}
                <Text
                  fw={700}
                  span
                  c={'cerise.7'}
                  td={'underline'}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setShowResetPasswordModal(true)}
                >
                  {'Reset It'}
                </Text>
              </Text>
            )}
          </Alert.Transition>
        </Stack>

        {signInButton?.(handleCreateAccount, isLoading)}
      </Stack>
      <ResetPasswordModal
        opened={showResetPasswordModal}
        onClose={() => setShowResetPasswordModal(false)}
      />
    </>
  );
};

type CreateAccountCardProps = {
  isCardExpanded?: boolean;
};
const CreateAccountCard = ({
  initialEmail,
  cause_uuid,
  isCardExpanded,
  onSuccess,
  isEmailDisabled,
  toggleSignIn,
}: CreateAccountProps & CreateAccountCardProps) => {
  return (
    <CollapsibleCard isCardExpanded={isCardExpanded} headerTitle={'Create An Account'}>
      <CreateAccountForm
        cause_uuid={cause_uuid}
        initialEmail={initialEmail}
        onSuccess={(user: User) => {
          onSuccess?.(user);
        }}
        isEmailDisabled={isEmailDisabled}
        toggleSignIn={toggleSignIn}
        signInButton={(onClick, isLoading) => (
          <ButtonMantine variant="gradient" onClick={onClick} disabled={isLoading}>
            Create Account
          </ButtonMantine>
        )}
      />
    </CollapsibleCard>
  );
};

export { CreateAccountCard, CreateAccountForm };
